import {
  PRODUCT_FEEDBACK_LIMITS,
  PRODUCT_FEEDBACK_SANITIZER_VERSION,
  PRODUCT_FEEDBACK_SCHEMA_VERSION,
  PRODUCT_FEEDBACK_WARNING_PREFIXES,
  type ProductFeedbackDiagnosticEvent,
  type ProductFeedbackDiagnosticLevel,
  type ProductFeedbackDiagnosticSource,
  type ProductFeedbackDiagnosticsPayload,
} from '../types/productFeedback';
import {
  getProductFeedbackUtf8ByteLength,
  sanitizeProductFeedbackDiagnosticEvent,
  type ProductFeedbackDiagnosticEventInput,
} from '../utils/productFeedbackSanitizer';

interface RecorderConsole {
  error: (...data: unknown[]) => void;
  warn: (...data: unknown[]) => void;
}

interface RecorderEventTarget {
  addEventListener: (type: string, listener: EventListener) => void;
  removeEventListener: (type: string, listener: EventListener) => void;
}

export interface ProductFeedbackDiagnosticRecorderOptions {
  consoleTarget?: RecorderConsole;
  eventTarget?: RecorderEventTarget;
  now?: () => number;
  routePath?: () => string;
  deduplicationWindowMs?: number;
}

export interface ProductFeedbackDiagnosticSnapshotOptions {
  included?: boolean;
  capturedAtClient?: string;
}

export interface ProductFeedbackDiagnosticsHealthCheckResult {
  healthy: boolean;
  recorderActive: boolean;
  listenersActive: boolean;
  sanitizerPass: boolean;
  serializationPass: boolean;
}

export interface ProductFeedbackDiagnosticRecorder {
  start: () => void;
  stop: () => void;
  clear: () => void;
  isRunning: () => boolean;
  record: (input: ProductFeedbackDiagnosticEventInput) => boolean;
  checkHealth: () => ProductFeedbackDiagnosticsHealthCheckResult;
  getSnapshot: (
    options?: ProductFeedbackDiagnosticSnapshotOptions
  ) => ProductFeedbackDiagnosticsPayload;
}

const cloneEvent = (event: ProductFeedbackDiagnosticEvent): ProductFeedbackDiagnosticEvent => ({
  ...event,
  details: [...event.details],
  sources: [...event.sources],
});

const eventBytes = (events: readonly ProductFeedbackDiagnosticEvent[]): number =>
  getProductFeedbackUtf8ByteLength(JSON.stringify(events));

const eventSignature = (event: ProductFeedbackDiagnosticEvent): string =>
  JSON.stringify({
    level: event.level,
    routePath: event.routePath,
    message: event.message,
    details: event.details,
    stack: event.stack,
  });

const eventTimestamp = (event: ProductFeedbackDiagnosticEvent): number =>
  new Date(event.occurredAtClient).getTime();

const isAllowedWarning = (args: readonly unknown[]): boolean => {
  const firstString = args.find((value) => typeof value === 'string');
  return (
    typeof firstString === 'string' &&
    PRODUCT_FEEDBACK_WARNING_PREFIXES.some((prefix) => firstString.startsWith(prefix))
  );
};

const getWarningArguments = (args: readonly unknown[]): unknown[] => {
  const firstString = args.find((value) => typeof value === 'string');
  const errors = args.filter((value) => value instanceof Error);
  return typeof firstString === 'string' ? [firstString, ...errors] : errors;
};

const getDefaultEventTarget = (): RecorderEventTarget | undefined =>
  typeof window === 'undefined' ? undefined : window;

const getDefaultRoutePath = (): string =>
  typeof window === 'undefined' ? '/' : window.location.pathname;

export const createProductFeedbackDiagnosticRecorder = (
  options: ProductFeedbackDiagnosticRecorderOptions = {}
): ProductFeedbackDiagnosticRecorder => {
  const consoleTarget = options.consoleTarget || console;
  const eventTarget = options.eventTarget || getDefaultEventTarget();
  const now = options.now || Date.now;
  const routePath = options.routePath || getDefaultRoutePath;
  const deduplicationWindowMs = options.deduplicationWindowMs ?? 1000;

  let events: ProductFeedbackDiagnosticEvent[] = [];
  let running = false;
  let droppedEventCount = 0;
  let clientRedactionCount = 0;
  let omittedObjectCount = 0;
  let truncated = false;
  let originalError: RecorderConsole['error'] | null = null;
  let originalWarn: RecorderConsole['warn'] | null = null;
  let wrappedError: RecorderConsole['error'] | null = null;
  let wrappedWarn: RecorderConsole['warn'] | null = null;
  let errorListenerAttached = false;
  let rejectionListenerAttached = false;

  const pruneExpired = () => {
    const cutoff = now() - PRODUCT_FEEDBACK_LIMITS.diagnosticWindowMs;
    events = events.filter((event) => eventTimestamp(event) >= cutoff);
  };

  const enforceBounds = () => {
    pruneExpired();

    while (events.length > PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEvents) {
      events.shift();
      droppedEventCount += 1;
      truncated = true;
    }

    while (
      events.length > 0 &&
      eventBytes(events) > PRODUCT_FEEDBACK_LIMITS.maxPersistedDiagnosticsBytes
    ) {
      events.shift();
      droppedEventCount += 1;
      truncated = true;
    }
  };

  const record = (input: ProductFeedbackDiagnosticEventInput): boolean => {
    try {
      const sanitized = sanitizeProductFeedbackDiagnosticEvent(input);
      const incoming = sanitized.event;
      const incomingTime = eventTimestamp(incoming);
      const signature = eventSignature(incoming);
      const duplicate = [...events]
        .reverse()
        .find(
          (event) =>
            eventSignature(event) === signature &&
            Math.abs(incomingTime - eventTimestamp(event)) <= deduplicationWindowMs
        );

      clientRedactionCount += sanitized.redactionCount;
      omittedObjectCount += sanitized.omittedObjectCount;
      truncated = truncated || sanitized.truncated;

      if (duplicate) {
        duplicate.occurrences += 1;
        if (!duplicate.sources.includes(incoming.source)) {
          duplicate.sources.push(incoming.source);
        }
      } else {
        events.push(incoming);
      }

      enforceBounds();
      return true;
    } catch {
      return false;
    }
  };

  const checkHealth = (): ProductFeedbackDiagnosticsHealthCheckResult => {
    const recorderActive = running;
    const listenersActive = Boolean(
      running &&
        eventTarget &&
        errorListenerAttached &&
        rejectionListenerAttached &&
        wrappedError &&
        wrappedWarn &&
        consoleTarget.error === wrappedError &&
        consoleTarget.warn === wrappedWarn
    );
    let sanitizerPass = false;
    let serializationPass = false;

    try {
      const probe = sanitizeProductFeedbackDiagnosticEvent({
        source: 'console_error',
        level: 'error',
        occurredAtClient: new Date(now()).toISOString(),
        routePath: routePath(),
        arguments: ['product-feedback-diagnostics-self-test'],
      });
      sanitizerPass = probe.event.message === 'product-feedback-diagnostics-self-test';
      const serializedBytes = eventBytes([probe.event]);
      serializationPass =
        Number.isFinite(serializedBytes) &&
        serializedBytes > 0 &&
        serializedBytes <= PRODUCT_FEEDBACK_LIMITS.maxPersistedDiagnosticsBytes;
    } catch {
      // A failed probe is reported locally and never committed to the diagnostic buffer.
    }

    return {
      healthy: recorderActive && listenersActive && sanitizerPass && serializationPass,
      recorderActive,
      listenersActive,
      sanitizerPass,
      serializationPass,
    };
  };

  const capture = (
    source: ProductFeedbackDiagnosticSource,
    level: ProductFeedbackDiagnosticLevel,
    args: readonly unknown[]
  ) => {
    record({
      source,
      level,
      occurredAtClient: new Date(now()).toISOString(),
      routePath: routePath(),
      arguments: args,
    });
  };

  const handleWindowError: EventListener = (event) => {
    try {
      const errorEvent = event as ErrorEvent;
      capture('window_error', 'error', [errorEvent.error || errorEvent.message || 'Window error']);
    } catch {
      // Recorder failures must never affect the host application's error handling.
    }
  };

  const handleUnhandledRejection: EventListener = (event) => {
    try {
      const rejectionEvent = event as PromiseRejectionEvent;
      capture('unhandled_rejection', 'error', [rejectionEvent.reason]);
    } catch {
      // Recorder failures must never affect the host application's rejection handling.
    }
  };

  const start = () => {
    if (running) {
      return;
    }

    originalError = consoleTarget.error;
    originalWarn = consoleTarget.warn;

    wrappedError = (...args: unknown[]) => {
      Reflect.apply(originalError as RecorderConsole['error'], consoleTarget, args);
      try {
        capture('console_error', 'error', args);
      } catch {
        // Never log recorder failures: logging here could recurse into this wrapper.
      }
    };

    wrappedWarn = (...args: unknown[]) => {
      Reflect.apply(originalWarn as RecorderConsole['warn'], consoleTarget, args);
      try {
        if (isAllowedWarning(args)) {
          capture('console_warn', 'warn', getWarningArguments(args));
        }
      } catch {
        // Never log recorder failures: logging here could recurse into this wrapper.
      }
    };

    consoleTarget.error = wrappedError;
    consoleTarget.warn = wrappedWarn;
    try {
      eventTarget?.addEventListener('error', handleWindowError);
      errorListenerAttached = Boolean(eventTarget);
      eventTarget?.addEventListener('unhandledrejection', handleUnhandledRejection);
      rejectionListenerAttached = Boolean(eventTarget);
      running = true;
    } catch {
      if (errorListenerAttached) eventTarget?.removeEventListener('error', handleWindowError);
      if (rejectionListenerAttached) {
        eventTarget?.removeEventListener('unhandledrejection', handleUnhandledRejection);
      }
      if (wrappedError && consoleTarget.error === wrappedError && originalError) {
        consoleTarget.error = originalError;
      }
      if (wrappedWarn && consoleTarget.warn === wrappedWarn && originalWarn) {
        consoleTarget.warn = originalWarn;
      }
      originalError = null;
      originalWarn = null;
      wrappedError = null;
      wrappedWarn = null;
      errorListenerAttached = false;
      rejectionListenerAttached = false;
    }
  };

  const stop = () => {
    if (!running) {
      return;
    }

    eventTarget?.removeEventListener('error', handleWindowError);
    eventTarget?.removeEventListener('unhandledrejection', handleUnhandledRejection);
    errorListenerAttached = false;
    rejectionListenerAttached = false;

    if (wrappedError && consoleTarget.error === wrappedError && originalError) {
      consoleTarget.error = originalError;
    }
    if (wrappedWarn && consoleTarget.warn === wrappedWarn && originalWarn) {
      consoleTarget.warn = originalWarn;
    }

    originalError = null;
    originalWarn = null;
    wrappedError = null;
    wrappedWarn = null;
    running = false;
  };

  const clear = () => {
    events = [];
    droppedEventCount = 0;
    clientRedactionCount = 0;
    omittedObjectCount = 0;
    truncated = false;
  };

  const getSnapshot = (
    snapshotOptions: ProductFeedbackDiagnosticSnapshotOptions = {}
  ): ProductFeedbackDiagnosticsPayload => {
    const included = snapshotOptions.included ?? true;
    const capturedAtClient = snapshotOptions.capturedAtClient
      ? new Date(snapshotOptions.capturedAtClient).toISOString()
      : new Date(now()).toISOString();

    if (!included) {
      return {
        schemaVersion: PRODUCT_FEEDBACK_SCHEMA_VERSION,
        included: false,
        capturedAtClient,
        windowMs: PRODUCT_FEEDBACK_LIMITS.diagnosticWindowMs,
        events: [],
        droppedEventCount: 0,
        clientRedactionCount: 0,
        serverRedactionCount: 0,
        omittedObjectCount: 0,
        bytes: eventBytes([]),
        truncated: false,
        sanitizerVersion: PRODUCT_FEEDBACK_SANITIZER_VERSION,
      };
    }

    enforceBounds();
    const snapshotEvents = events.map(cloneEvent);

    return {
      schemaVersion: PRODUCT_FEEDBACK_SCHEMA_VERSION,
      included: true,
      capturedAtClient,
      windowMs: PRODUCT_FEEDBACK_LIMITS.diagnosticWindowMs,
      events: snapshotEvents,
      droppedEventCount,
      clientRedactionCount,
      serverRedactionCount: 0,
      omittedObjectCount,
      bytes: eventBytes(snapshotEvents),
      truncated,
      sanitizerVersion: PRODUCT_FEEDBACK_SANITIZER_VERSION,
    };
  };

  return {
    start,
    stop,
    clear,
    isRunning: () => running,
    record,
    checkHealth,
    getSnapshot,
  };
};

let sharedRecorder: ProductFeedbackDiagnosticRecorder | null = null;

export const getProductFeedbackDiagnosticRecorder = (): ProductFeedbackDiagnosticRecorder => {
  if (!sharedRecorder) {
    sharedRecorder = createProductFeedbackDiagnosticRecorder();
  }

  return sharedRecorder;
};
